import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { uploadFile, fileViewUrl } from "../lib/files";

/**
 * The forced first-login onboarding wizard: Profile -> Documents -> Policies.
 * A gated worker is routed here (App.tsx) and the API is locked to onboarding
 * endpoints until they finish, so the three steps are the whole world until then.
 */

interface OnboardingStatus {
    profile: { required: Array<{ field: string; filled: boolean }>; complete: boolean };
    documentTypes: Array<{ id: string; name: string; expires: boolean; required: boolean; uploaded: boolean }>;
    documents: Array<{ id: string; type_name: string; expiry_date: string | null; verification_status: string }>;
    requiredDocumentsComplete: boolean;
    policies: Array<{ policyGroupId: string; policyId: string; name: string; version: string; required: boolean; acknowledged: boolean }>;
    requiredPoliciesComplete: boolean;
    complete: boolean;
}

interface PolicyRow { id: string; name: string; file_id: string | null; body_url: string | null; }

const PROFILE_FIELDS: Array<{ name: string; label: string; type: string; required: boolean }> = [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "date_of_birth", label: "Birthday", type: "date", required: true },
    { name: "gender", label: "Gender", type: "text", required: true },
    { name: "address", label: "Home address", type: "text", required: true },
    { name: "emergency_contact", label: "Emergency contact name", type: "text", required: true },
    { name: "emergency_contact_relationship", label: "Relationship", type: "text", required: true },
    { name: "emergency_phone", label: "Emergency contact phone", type: "tel", required: true },
];

const STEPS = ["Profile", "Documents", "Policies"];

export function OnboardingWizard() {
    const [step, setStep] = useState(0);
    const [status, setStatus] = useState<OnboardingStatus | null>(null);
    const [policies, setPolicies] = useState<PolicyRow[]>([]);
    const [profile, setProfile] = useState<Record<string, string>>({});
    const [opened, setOpened] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        const [s, p] = await Promise.all([
            api.get<OnboardingStatus>("/me/onboarding"),
            api.get<Record<string, string>>("/me/profile"),
        ]);
        setStatus(s);
        setProfile((prev) => ({ ...p, ...prev }));
    };

    useEffect(() => {
        void reload();
        void api.get<PolicyRow[]>("/policies").then(setPolicies).catch(() => setPolicies([]));
    }, []);

    const policyLink = useMemo(() => new Map(policies.map((p) => [p.id, p])), [policies]);

    const saveProfile = async () => {
        setBusy(true); setError(null);
        try {
            const body: Record<string, string> = {};
            for (const f of PROFILE_FIELDS) body[f.name] = profile[f.name] ?? "";
            await api.put("/me/profile", body);
            await reload();
            setStep(1);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not save your profile");
        } finally { setBusy(false); }
    };

    const finish = async () => {
        setBusy(true); setError(null);
        try {
            await api.post("/me/onboarding/complete", {});
            // Hard navigation so AuthContext re-bootstraps /auth/me — with
            // onboardingRequired now false, Home stops redirecting here.
            window.location.href = "/";
        } catch (e) {
            setError(e instanceof Error ? e.message : "Some required items are still outstanding.");
            await reload();
        } finally { setBusy(false); }
    };

    if (!status) return <div className="onboard-wrap"><p className="muted">Loading your onboarding…</p></div>;

    return (
        <div className="onboard-wrap">
            <div className="onboard-head">
                <h1>Welcome — let's get you set up</h1>
                <p className="muted">Complete these steps to unlock your account.</p>
                <ol className="stepper">
                    {STEPS.map((label, i) => (
                        <li key={label} className={`stepper-item ${i === step ? "current" : ""} ${i < step ? "done" : ""}`}>
                            <span className="stepper-dot">{i < step ? "✓" : i + 1}</span>{label}
                        </li>
                    ))}
                </ol>
            </div>

            {error && <div className="error-banner">{error}</div>}

            <div className="card"><div className="card-body">
                {step === 0 && (
                    <>
                        <h2>Your profile</h2>
                        <div className="form-grid">
                            {PROFILE_FIELDS.map((f) => (
                                <div className="field" key={f.name}>
                                    <label htmlFor={`ob-${f.name}`}>{f.label}{f.required && <span className="req"> *</span>}</label>
                                    <input
                                        id={`ob-${f.name}`}
                                        type={f.type}
                                        value={profile[f.name] ?? ""}
                                        onChange={(e) => setProfile((p) => ({ ...p, [f.name]: e.target.value }))}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="onboard-actions">
                            <button className="btn" disabled={busy} onClick={saveProfile}>Save & continue</button>
                        </div>
                    </>
                )}

                {step === 1 && (
                    <DocumentsStep status={status} onChanged={reload} onBack={() => setStep(0)} onNext={() => setStep(2)} />
                )}

                {step === 2 && (
                    <>
                        <h2>Policies</h2>
                        <p className="muted">Open and read each policy, then confirm you have understood it.</p>
                        {status.policies.length === 0 && <p className="muted">No policies assigned.</p>}
                        {status.policies.map((p) => {
                            const link = policyLink.get(p.policyId);
                            const href = link?.file_id ? fileViewUrl(link.file_id) : link?.body_url ?? undefined;
                            const canTick = opened.has(p.policyId) || p.acknowledged;
                            return (
                                <div className="policy-ack-row" key={p.policyId}>
                                    <div>
                                        <b>{p.name}</b> <span className="muted">v{p.version}{p.required ? " · required" : " · optional"}</span>
                                    </div>
                                    <div className="policy-ack-controls">
                                        <a className="btn ghost" href={href} target="_blank" rel="noreferrer"
                                            onClick={() => setOpened((s) => new Set(s).add(p.policyId))}>Open</a>
                                        <label className="check">
                                            <input
                                                type="checkbox"
                                                checked={p.acknowledged}
                                                disabled={p.acknowledged || !canTick}
                                                onChange={async () => {
                                                    await api.post(`/policies/${p.policyId}/acknowledge`, {});
                                                    await reload();
                                                }}
                                            />
                                            <span>I have read and understood</span>
                                        </label>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="onboard-actions">
                            <button className="btn ghost" onClick={() => setStep(1)}>Back</button>
                            <button className="btn" disabled={busy || !status.complete} onClick={finish}>
                                {status.complete ? "Finish onboarding" : "Complete required items to finish"}
                            </button>
                        </div>
                    </>
                )}
            </div></div>
        </div>
    );
}

function DocumentsStep({ status, onChanged, onBack, onNext }: {
    status: OnboardingStatus; onChanged: () => Promise<void>; onBack: () => void; onNext: () => void;
}) {
    const [error, setError] = useState<string | null>(null);
    const [busyType, setBusyType] = useState<string | null>(null);
    const [expiry, setExpiry] = useState<Record<string, string>>({});

    const upload = async (typeId: string, expires: boolean, file: File) => {
        setError(null);
        if (expires && !expiry[typeId]) { setError("Please set an expiry date for this document type."); return; }
        setBusyType(typeId);
        try {
            const stored = await uploadFile(file, "general");
            await api.post("/me/documents", {
                fileId: stored.id, documentTypeId: typeId, expiryDate: expires ? expiry[typeId] : null,
            });
            await onChanged();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Upload failed");
        } finally { setBusyType(null); }
    };

    return (
        <>
            <h2>Documents</h2>
            <p className="muted">Upload your documents. Types marked required must be provided to finish.</p>
            {error && <div className="error-banner">{error}</div>}
            {status.documentTypes.map((t) => (
                <div className="doc-upload-row" key={t.id}>
                    <div>
                        <b>{t.name}</b>{" "}
                        <span className="muted">{t.required ? "required" : "optional"}{t.expires ? " · expires" : ""}</span>
                        {t.uploaded && <span className="chip pass" style={{ marginLeft: 6 }}>uploaded</span>}
                    </div>
                    <div className="doc-upload-controls">
                        {t.expires && (
                            <input type="date" aria-label={`${t.name} expiry`}
                                value={expiry[t.id] ?? ""} onChange={(e) => setExpiry((s) => ({ ...s, [t.id]: e.target.value }))} />
                        )}
                        <label className="btn ghost">
                            {busyType === t.id ? "Uploading…" : "Upload"}
                            <input type="file" hidden disabled={busyType === t.id}
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(t.id, t.expires, f); e.target.value = ""; }} />
                        </label>
                    </div>
                </div>
            ))}
            <div className="onboard-actions">
                <button className="btn ghost" onClick={onBack}>Back</button>
                <button className="btn" disabled={!status.requiredDocumentsComplete} onClick={onNext}>
                    {status.requiredDocumentsComplete ? "Continue" : "Upload required documents to continue"}
                </button>
            </div>
        </>
    );
}
